import React from 'react';
import Chart from 'react-apexcharts';
import { Paper, Typography, Box, GlobalStyles } from '@mui/material';
import dayjs from 'dayjs';
import { getDomainLevelDistribution, levelColors } from './CategoryLevels';

const BarChartLevel = ({ data }) => {
  const domainSet = new Set();
  const order = [
    'Social Emotional',
    'Play Behaviour',
    'Cognitive Skills',
    'Receptive Language',
    'Expressive Language',
    'Gross Motor',
    'Fine Motor',
    'Activities of Daily Living',
    'Sensory',
    'Behaviour'
  ];
  
  const dateSet = new Set();
  const domainDateMap = {};

  data.forEach((entry) => {
    const date = dayjs(entry.submittedAt).format('YYYY-MM-DD');
    dateSet.add(date);

    entry.domains.forEach((d) => {
      domainSet.add(d.domain);
      if (!domainDateMap[d.domain]) domainDateMap[d.domain] = {};
      domainDateMap[d.domain][date] = { skillsAchieved: d.skillsAchieved };
    });
  });

  const allDomains = Array.from(domainSet).filter(
    (d) => d !== 'Sensory' && d !== 'Behaviour'
  ).sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  const allDates = Array.from(dateSet).sort();

  const lastDataPerDomain = {};
  allDomains.forEach((domain) => {
    const availableDates = allDates.filter(date => domainDateMap[domain]?.[date]);
    const lastDate = availableDates.length ? availableDates[availableDates.length - 1] : null;

    if (lastDate) {
      const { skillsAchieved } = domainDateMap[domain][lastDate];
      lastDataPerDomain[domain] = getDomainLevelDistribution(domain, skillsAchieved);
    } else {
      lastDataPerDomain[domain] = { 1: 0, 2: 0, 3: 0, 4: 0 };
    }
  });

  const levels = [1, 2, 3, 4];

  // Instead of stacking, create grouped bars per level
  const series = levels.map(lvl => ({
    name: `Level ${lvl}`,
    data: allDomains.map(domain => lastDataPerDomain[domain][lvl] || 0),
  }));

  // const options = {
  //   chart: { type: 'bar', stacked: true },
  //   plotOptions: { 
  //     bar: { horizontal: false, columnWidth: '60%' } // grouped vertical bars
  //   },
  //   xaxis: { categories: allDomains, title: { text: 'Domains' } },
  //   yaxis: { 
  //     title: { text: 'Levels' },
  //     labels: {
  //       formatter: (val) => val.toFixed(2)
  //     }
  //   },
  //   dataLabels: {
  //     enabled: true,
  //     formatter: (val) => (val > 0 ? val.toFixed(2) : ''),
  //   },
  //   tooltip: {
  //     y: { formatter: val => (val > 0 ? val.toFixed(2) : 'No data') }
  //   },
  //   colors: levels.map(lvl => levelColors[lvl]),
  //   legend: { show: true, position: 'top' },
  // };

  const options = {
    chart: { type: 'bar', stacked: true },
    plotOptions: { 
      bar: { horizontal: false, columnWidth: '60%' }
    },
    xaxis: { 
      categories: allDomains, 
      labels: {
        rotate: -45, // rotate labels
        rotateAlways: true, // ensures rotation always applies
        hideOverlappingLabels: true, // optional, hides if overlapping
        trim: false, // optional, keeps full text
        style: {
          fontSize: '7px',
        }
      }
    },
    yaxis: { show: false }, // hide y-axis values
    dataLabels: {
      enabled: true,
      formatter: (_, { seriesIndex }) => `Level ${levels[seriesIndex]}`, // 🔹 show Level instead of numbers
    },
    tooltip: {
      y: { 
        formatter: () => ''
      },
      x: {
        formatter: (val, { seriesIndex }) => {
          return `Level ${levels[seriesIndex]}`; // 🔹 tooltip shows Level only
        }
      },
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const domain = w.globals.labels[dataPointIndex]; // get domain name
        const level = `Level ${levels[seriesIndex]}`;
        return `<div class="apex-tooltip">
                  <strong>${domain}</strong>:
                  ${level}
                </div>`;
      }
    },
    colors: levels.map(lvl => levelColors[lvl]),
    legend: { show: true, position: 'top' },
  };

  return (
    <Paper elevation={3} sx={{ p: 2, my: 4 }}>
      <GlobalStyles styles={{
        '.apex-tooltip': {
          padding: '10px'
        }
      }} />
      <Box sx={{ height: 400 }}>
        {series.every(s => s.data.every(v => v === 0)) ? (
          <Typography>No data available.</Typography>
        ) : (
          <Chart options={options} series={series} type="bar" height="100%" />
        )}
      </Box>
    </Paper>
  );
};

export default BarChartLevel;
